import React, { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Tag, Eye, EyeOff, FileEdit } from "lucide-react";
import { adminApi, formatErr } from "@/admin/api";
import { Panel, AdminInput, AdminSelect, AdminTextarea } from "@/admin/components/ui";

const PACKAGES = [
  { n: 1,  key: "single_photo_credits",  label: "1 Photo" },
  { n: 5,  key: "five_photo_credits",    label: "5 Photos" },
  { n: 10, key: "ten_photo_credits",     label: "10 Photos" },
  { n: 15, key: "fifteen_photo_credits", label: "15 Photos" },
  { n: 20, key: "twenty_photo_credits",  label: "20 Photos" },
];

const STATUS_OPTIONS = [
  { value: "active",   label: "Active",   sub: "Visible to customers · selectable",  tone: "text-[#7fe6ff]" },
  { value: "draft",    label: "Draft",    sub: "Admin-only · not visible publicly",  tone: "text-[#ffd479]" },
  { value: "disabled", label: "Disabled", sub: "Hidden everywhere",                  tone: "text-[#ff9fcf]" },
];

const empty = {
  single_photo_credits: 0,
  five_photo_credits: 0,
  ten_photo_credits: 0,
  fifteen_photo_credits: 0,
  twenty_photo_credits: 0,
  status: "draft",
  notes: "",
};

const StatusBadge = ({ status }) => {
  const o = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[1];
  const Icon = status === "active" ? Eye : status === "disabled" ? EyeOff : FileEdit;
  return (
    <span data-testid={`pricing-status-badge-${status}`} className={`inline-flex items-center gap-1.5 text-[10px] tracking-luxe uppercase px-2.5 py-1 rounded-full glass-strong border border-white/10 ${o.tone}`}>
      <SEO title="AdminEventPricing · WelkomBiz Photography" noindex />
      <Icon className="w-3 h-3" /> {o.label}
    </span>
  );
};

export default function AdminEventPricing() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [form, setForm] = useState(empty);
  const [persisted, setPersisted] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/events/${id}/pricing`);
      setEvent(r.data.event);
      if (r.data.pricing) {
        setForm({ ...empty, ...r.data.pricing });
        setPersisted(r.data.pricing);
      } else {
        setForm(empty);
        setPersisted(null);
      }
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

  const onNum = (key) => (e) => {
    const v = Math.max(0, parseInt(e.target.value || "0", 10));
    setForm({ ...form, [key]: isNaN(v) ? 0 : v });
  };

  const save = async () => {
    for (const p of PACKAGES) {
      if (form[p.key] === "" || form[p.key] === null || form[p.key] === undefined || isNaN(Number(form[p.key]))) {
        toast.error(`Enter a valid credit value for "${p.label}".`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        single_photo_credits: Number(form.single_photo_credits),
        five_photo_credits: Number(form.five_photo_credits),
        ten_photo_credits: Number(form.ten_photo_credits),
        fifteen_photo_credits: Number(form.fifteen_photo_credits),
        twenty_photo_credits: Number(form.twenty_photo_credits),
        status: form.status,
        notes: form.notes || "",
      };
      const r = await adminApi.put(`/admin/events/${id}/pricing`, payload);
      setPersisted(r.data);
      toast.success("Pricing saved.");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-white/40">Loading pricing…</p>;
  if (!event) return <p className="text-sm text-white/55">Event not found.</p>;

  const isPublic = persisted?.status === "active";

  return (
    <div data-testid="page-admin-event-pricing" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <Link to="/admin/events" data-testid="pricing-back" className="inline-flex items-center gap-2 text-[10px] tracking-luxe uppercase text-white/55 hover:text-white">
          <ArrowLeft className="w-3.5 h-3.5" /> All Events
        </Link>
        <div className="flex-1" />
        {persisted && <StatusBadge status={persisted.status} />}
      </div>

      <Panel testId="pricing-event-header">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] tracking-luxe uppercase text-white/45">— Event Pricing</p>
            <h1 className="font-display text-3xl sm:text-4xl text-chrome truncate">{event.title}</h1>
            <p className="text-[11px] tracking-luxe uppercase text-white/55 mt-1">{event.date} · {event.category}</p>
          </div>
          <Link
            to={`/events/${event.slug}`}
            target="_blank"
            data-testid="pricing-view-public"
            className="btn-ghost !py-2 !px-4 !text-[10px] inline-flex"
          >
            <Tag className="w-3.5 h-3.5" /> View Public Page
          </Link>
        </div>
        <p className="text-xs text-white/50 mt-4">
          Set the credit cost for each photo package. Customers see these packages on the public event page only when the
          status is <strong className="text-[#7fe6ff]">Active</strong>.
          <strong className="text-[#ffd479]"> Draft</strong> and <strong className="text-[#ff9fcf]">Disabled</strong> stay admin-only.
          Pricing applies in Phase 6 checkout — Phase 5 is configuration only.
        </p>
      </Panel>

      <Panel title="Credit Packages" testId="pricing-packages-panel">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {PACKAGES.map((p) => (
            <div key={p.key} className="glass chrome-border rounded-md p-4">
              <p className="font-display text-3xl text-chrome">{p.n}</p>
              <p className="text-[10px] tracking-luxe uppercase text-white/45 mt-1 mb-3">{p.label}</p>
              <AdminInput
                data-testid={`pricing-input-${p.key}`}
                label="Credit Cost"
                type="number"
                min={0}
                value={form[p.key]}
                onChange={onNum(p.key)}
              />
            </div>
          ))}
        </div>
        <p className="text-[10px] tracking-luxe uppercase text-white/40 mt-4">
          Tip · larger packages should typically offer a discount per photo to encourage bulk selection.
        </p>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel title="Visibility" testId="pricing-visibility-panel">
          <AdminSelect
            data-testid="pricing-input-status"
            label="Status"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="bg-black">{o.label}</option>
            ))}
          </AdminSelect>
          <div className="mt-3 space-y-2">
            {STATUS_OPTIONS.map((o) => (
              <div key={o.value} className={`flex items-start gap-2 text-xs ${form.status === o.value ? "text-white" : "text-white/45"}`}>
                <span className={`mt-1 w-1.5 h-1.5 rounded-full ${o.tone.replace("text-", "bg-")}`} />
                <div>
                  <p className={`tracking-luxe uppercase text-[10px] ${o.tone}`}>{o.label}</p>
                  <p>{o.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Admin Notes" testId="pricing-notes-panel">
          <AdminTextarea
            data-testid="pricing-input-notes"
            label="Internal Notes (not shown to customers)"
            rows={5}
            value={form.notes || ""}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Discount logic, season pricing strategy, package rationale…"
          />
        </Panel>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={save}
          disabled={saving}
          data-testid="pricing-save"
          className="btn-primary !py-2.5 !px-5 !text-[11px] disabled:opacity-60"
        >
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Pricing"}
        </button>
        {persisted && (
          <p data-testid="pricing-persisted-info" className="text-[11px] tracking-luxe uppercase text-white/45 self-center">
            Last updated · {persisted.updated_at ? new Date(persisted.updated_at).toLocaleString("en-ZA",{dateStyle:"medium",timeStyle:"short"}) : "—"}
            {persisted.updated_by_admin_email ? ` · by ${persisted.updated_by_admin_email}` : ""}
            {isPublic ? " · currently public" : " · not public"}
          </p>
        )}
      </div>
    </div>
  );
}
